import React, { useCallback, useMemo, useRef } from "react"
import { useSelector } from "react-redux"
import {
  AuthStatus,
  haLoginSession,
  loginSession,
  logoutSession,
  selectAuthenticationStatus,
  totpLoginSession,
} from "../authentication/authenticationSlice"
import HALogo from "./HALogo.svg"
import { useAppDispatch } from "../../reduxStore/hooks"
import careConnectLogo from "./../../assets/careConnectLogo.svg"
import { useCMSReady } from "../../domain/CMS/useCMSReady"
import {
  BASE_ROUTE,
  GETTING_STARTED_ROUTE,
  SITE_OUTAGE_RESTRICTED_ROUTE,
  TASKBOARD_BASE_ROUTE,
} from "../../routes"
import { useNavigateWithQueryString } from "../../App"
import { useSlideOver } from "../useSlideOver"
import { ProviderProfileSlideOverContent } from "../profile/ProviderProfileSlideOverContent"
import { PracticeProfileSlideOverContent } from "../profile/PracticeProfileSlideOverContent"
import { modalComponentRegistry } from "../../domain/Profile/modalComponentRegistry"
import { useLocation } from "react-router"
import { DropDownButton } from "../shared/components/DropDownButton"
import { DropDownButtonItem } from "../shared/components/DropDownButtonItem"
import { selectIsUserOnboarded } from "../../domain/Practices/selectors"
import { selectUser } from "../shared/selectors"
import {
  selectSiteOutageMessage,
  selectIsAffiliateRelations,
} from "../../domain/Account/userSlice"
import ContactUs from "../../pages/ContactUs"
import About from "../../pages/About"
import { ContentLayoutComponent } from "../shared/components/ContentLayoutComponent"
import { getHelpPageForPath } from "../../utils/helpSectionMapping"
import { removeAllBase64Images } from "../shared/localStorageBase64"
import { ButtonWrapper } from "../shared/components/ButtonWrapper"
import { Typography, typographyTypes } from "../shared/components/Typography"
import Card from "../shared/components/Card"
import { Modal } from "../shared/components/Modal"
import DropdownBox from "../shared/components/DropdownBox"
import Drawer from "../shared/components/Drawer"
import { NavigationLoadingOverlay } from "../navigation/NavigationLoadingOverlay"
import IconWithCaption from "../shared/components/IconWithCaption"
import { ContentLayoutContext } from "./useContentLayoutContext"

export const ContentLayout: React.FC<React.PropsWithChildren> = (props) => {
  const navigate = useNavigateWithQueryString()
  const dispatch = useAppDispatch()
  const location = useLocation()

  const userIsOnboarded = useSelector(selectIsUserOnboarded)
  const siteOutageMessage = useSelector(selectSiteOutageMessage)
  const isAffiliateRelations = useSelector(selectIsAffiliateRelations)

  const isAccessRestricted =
    !!siteOutageMessage?.isEnabled &&
    !!siteOutageMessage?.isUserAccessDisabled &&
    !isAffiliateRelations
  const { cms: cmsContent } = useCMSReady([
    "common",
    "contentLayout",
    "publicProfileSlide",
  ])
  const {
    contentLayout: contentLayoutText,
    common: commonText,
    publicProfileSlide: publicProfileText,
  } = cmsContent
  const withAuthenticationText = cmsContent.withAuthentication
  const authStatus = useSelector(selectAuthenticationStatus)
  const {
    showProfileModal,
    showProfileSidebar,
    handleSetShowProfileModal,
    handleSetShowProfileSidebar,
  } = useSlideOver()
  const user = useSelector(selectUser)
  const scrollableContainerRef = useRef(null)

  const [isSignInDropDownOpen, setIsSignInDropDownOpen] = React.useState(false)
  const [showContactUsModal, setShowContactUsModal] = React.useState(false)
  const [showAboutModal, setShowAboutModal] = React.useState(false)

  const userIsAuthenticated = authStatus === AuthStatus.Authenticated

  const handleCloseProfileModal = useCallback(() => {
    handleSetShowProfileModal()
  }, [handleSetShowProfileModal])

  const handleSubmitProfileModal = useCallback(() => {
    handleSetShowProfileModal()
  }, [handleSetShowProfileModal])

  const handleSetShowContactUsModal = () => {
    setShowContactUsModal((prev) => !prev)
  }

  const handleCloseAboutModal = useCallback(() => {
    setShowAboutModal(false)
  }, [])

  const brandComponent = (
    <ButtonWrapper
      onClick={() => {
        if (!userIsAuthenticated) {
          navigate(BASE_ROUTE)
        } else if (isAccessRestricted) {
          navigate(SITE_OUTAGE_RESTRICTED_ROUTE)
        } else if (userIsOnboarded) {
          navigate(TASKBOARD_BASE_ROUTE)
        }
      }}
      styleOverrides="ml-4"
    >
      <div className="flex flex-row items-center">
        <img
          className="h-7 mb-1"
          src={HALogo}
          alt={contentLayoutText!.brandLogoAltText}
        />
        <hr
          className={`hidden md:flex items-center border-solid border-l-1/2 border-gray-300 h-7 ml-[20px] ${userIsAuthenticated ? "hidden" : ""}`}
        ></hr>
        <div
          className={`hidden md:flex items-center ${userIsAuthenticated ? "hidden" : ""}`}
        >
          <img
            className="h-6 ml-4"
            src={careConnectLogo}
            alt={contentLayoutText!.coBrand}
          />
        </div>
      </div>
    </ButtonWrapper>
  )

  let topRightComponents
  if (userIsAuthenticated) {
    const onGettingStartedPage = location.pathname.includes(
      GETTING_STARTED_ROUTE,
    )
    topRightComponents = onGettingStartedPage
      ? [
          <ButtonWrapper
            key="sign-out"
            onClick={() => {
              removeAllBase64Images()
              dispatch(logoutSession())
            }}
            styleOverrides="items-center h-full"
          >
            <Typography
              variant={typographyTypes.NORMAL}
              styleOverrides="text-primary-blue"
              text={contentLayoutText?.signOut}
            />
          </ButtonWrapper>,
        ]
      : [
          <DropdownBox
            key="filter-0"
            triggerElement={
              <IconWithCaption
                caption={commonText!.account}
                icon={"userAccount"}
                fontSize="small"
                labelStyleOverrides="text-gray-700 cursor-pointer"
                iconStyleOverrides="fill-gray-500 !h-[25px] !w-[25px]"
              />
            }
            styleOverrides="mt-4 z-50 border-none shadow-md"
          >
            <Card borderStyleOverride="flex flex-col rounded p-0">
              <div
                style={{ borderBottom: "1px solid" }}
                className="!border-gray-100 flex flex-col py-3 px-4"
              >
                <Typography
                  variant={typographyTypes.NORMAL}
                  styleOverrides="font-semibold"
                  text={user?.name}
                />
                <Typography
                  variant={typographyTypes.NORMAL}
                  text={user?.email}
                />
              </div>
              <ButtonWrapper
                onClick={() => {
                  removeAllBase64Images()
                  dispatch(logoutSession())
                }}
                styleOverrides="py-3 px-4"
              >
                <Typography
                  variant={typographyTypes.NORMAL}
                  text={contentLayoutText?.signOut}
                  styleOverrides="text-primary-blue"
                />
              </ButtonWrapper>
            </Card>
          </DropdownBox>,
          <ButtonWrapper
            key="filter-1"
            onClick={() => {
              // Mapping returns '/helpPage' or '/helpPage#anchor'
              const mapped = getHelpPageForPath(location.pathname)
              window.open(
                `${window.location.origin}${mapped}`,
                "cchelppages",
                "noopener,noreferrer",
              )
            }}
            styleOverrides="py-0 ml-3"
          >
            <IconWithCaption
              caption={contentLayoutText!.help}
              icon={"helpIcon"}
              fontSize="small"
              labelStyleOverrides="text-gray-700 cursor-pointer"
              iconStyleOverrides="fill-gray-500 !h-[25px] !w-[25px]"
            />
          </ButtonWrapper>,
        ]
  } else {
    topRightComponents = [
      <div key="1">
        <DropDownButton
          buttonLabel={commonText!.signIn}
          open={isSignInDropDownOpen}
          setOpen={setIsSignInDropDownOpen}
        >
          <DropDownButtonItem
            label={withAuthenticationText!.affiliateSignInDropdownText}
            onClick={() => {
              removeAllBase64Images()
              dispatch(loginSession())
              setIsSignInDropDownOpen(false)
            }}
          />
          <DropDownButtonItem
            label={withAuthenticationText!.affiliateAppAuth}
            onClick={() => {
              removeAllBase64Images()
              dispatch(totpLoginSession())
              setIsSignInDropDownOpen(false)
            }}
          />
          <DropDownButtonItem
            label={withAuthenticationText!.haSignInDropdownText}
            onClick={() => {
              removeAllBase64Images()
              dispatch(haLoginSession(""))
              setIsSignInDropDownOpen(false)
            }}
          />
        </DropDownButton>
      </div>,
    ]
  }

  const topRightAlignment: "start" | "end" | "center" = userIsAuthenticated
    ? "end"
    : "center"

  const contentLayoutComponentProps = {
    brandComponent,
    topRightComponents,
    topRightItemAlignment: topRightAlignment,
    topRightXSpacing: "4",
    footerVisible: userIsAuthenticated,
    contactLink: handleSetShowContactUsModal,
    onAboutClick: () => {
      setShowAboutModal(true)
    },
    shouldShowAboutLink: true,
    legalPrivacyLink: "https://www.healthadvocate.com/site/privacy",
    termsLink: "https://www.healthadvocate.com/site/terms",
    text: {
      contactText: contentLayoutText!.contactText,
      aboutText: contentLayoutText!.aboutText,
      helpLinkText: contentLayoutText!.help,
      legalPrivacyText: contentLayoutText!.legalPrivacyText,
      termsText: contentLayoutText!.termsText,
      homeLinkText: contentLayoutText!.home,
      messageLinkText: contentLayoutText!.mail,
      menuText: contentLayoutText!.menu,
      skipToMainContentText: contentLayoutText!.skipToMainContentText,
    },
  }

  const profileSidebarId =
    showProfileSidebar.practiceId || showProfileSidebar.provider?.id
  const sideBarType = showProfileSidebar.practiceId ? "practice" : "provider"

  const ModalComponent = showProfileModal?.modalComponentRegistryKey
    ? modalComponentRegistry[showProfileModal.modalComponentRegistryKey.type]
    : null

  const contextValues = useMemo(
    () => ({
      scrollableContainerRef,
    }),
    [scrollableContainerRef],
  )

  return (
    <ContentLayoutContext.Provider value={contextValues}>
      <div className="relative box-border">
        <NavigationLoadingOverlay />
        <ContentLayoutComponent {...contentLayoutComponentProps}>
          {props.children}
          {!!profileSidebarId && (
            <Drawer
              isOpen={!!profileSidebarId}
              title={
                sideBarType === "provider"
                  ? "Provider Profile"
                  : "Practice Profile"
              }
              width="670px"
              position="right"
              onClose={handleSetShowProfileSidebar}
              backdropOpacity={0.1}
              containerStyleOverrides="px-0"
              titleStyleOverrides="px-6"
              closeOnBackdropClick={true}
            >
              {sideBarType === "provider" ? (
                <ProviderProfileSlideOverContent key="provider-profile-slide-over" />
              ) : (
                <PracticeProfileSlideOverContent key="practice-profile-slide-over" />
              )}
            </Drawer>
          )}
        </ContentLayoutComponent>

        <Modal
          text={{
            title: publicProfileText?.practicePhotosModalTitle || "",
          }}
          onCancel={
            !!showProfileModal?.modalComponentRegistryKey.onCancel
              ? handleCloseProfileModal
              : undefined
          }
          isOpen={!!showProfileModal}
          onSubmit={
            !!showProfileModal?.modalComponentRegistryKey.onSubmit
              ? handleSubmitProfileModal
              : undefined
          }
          onClose={handleCloseProfileModal}
          panelStyleOverrides={`min-w-[80%] min-h-[300px] !z-50 ${showProfileModal?.panelStyleOverrides || ""} `}
          titleStyleOverrides={`${showProfileModal?.titleStyleOverrides || ""} `}
        >
          {ModalComponent &&
            showProfileModal?.modalComponentRegistryKey.props && (
              <ModalComponent
                {...showProfileModal?.modalComponentRegistryKey.props}
              />
            )}
        </Modal>

        <Modal
          isOpen={showContactUsModal}
          onClose={handleSetShowContactUsModal}
          text={{ title: "" }}
          panelStyleOverrides="w-[600px] pl-[44px] pr-[34px] pt-[24px] pb-[24px]"
        >
          <ContactUs />
        </Modal>
        <Modal
          isOpen={showAboutModal}
          onClose={handleCloseAboutModal}
          text={{ title: "" }}
        >
          <About onClose={handleCloseAboutModal} />
        </Modal>
      </div>
    </ContentLayoutContext.Provider>
  )
}

export default ContentLayout
